import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../lib/utils';

const variants = cva(
  'flex justify-center items-center text-sm cursor-pointer',
  {
    variants: {
      variant: {
        // 기본 버튼 (버튼 색상: 회색, 텍스트 색상: 검정, 호버 시 회색에서 빨간색으로 변경, 테두리 색상: 회색, shadow 효과)
        normal: `
            bg-gscale-50 text-gscale-950 font-normal
            border-[rgba(13, 13, 14, 0.12)] border-solid border-[1px] transition-all duration-200
            hover:bg-gtred hover:text-gscale-50
            hover:border-gtred hover:shadow-[0_0_16px_0_rgba(224,64,20,0.5)]
        `,
        // 기본 버튼 shadow 제거
        normal_ne: `
            bg-gscale-50 text-gscale-950 font-normal
            border-[rgba(13, 13, 14, 0.12)] border-solid border-[1px] transition-all duration-200
            hover:bg-gtred hover:text-gscale-50
            hover:border-gtred
        `,
        dark: `
            bg-[#212121] text-gscale-50 font-normal
            border-[rgba(255, 255, 255, 0.08)] border-solid border-[1px] transition-all duration-200
            hover:bg-gtred hover:text-gscale-50
            hover:border-gtred hover:shadow-[0_0_16px_0_rgba(224,64,20,0.5)]
        `,
        dark_ne: `
            bg-[#212121] text-gscale-50 font-normal
            border-[rgba(255, 255, 255, 0.08)] border-solid border-[1px] transition-all duration-200
            hover:bg-gtred hover:text-gscale-50
            hover:border-gtred
        `,
        impact: `
            bg-gtred text-gscale-50 font-normal
            border-gtred border-solid border-[1px] transition-all duration-200
            hover:bg-gtred/80 hover:text-gscale-50
            hover:border-gtred/80 hover:shadow-[0_0_16px_0_rgba(224,64,20,0.5)]
        `,
        impact_ne: `
            bg-gtred text-gscale-50 font-normal
            border-gtred border-solid border-[1px] transition-all duration-200
            hover:bg-gtred/80 hover:text-gscale-50
            hover:border-gtred/80
        `,
        white: `
            bg-[#ffffff] text-gscale-950 font-normal
            border-[rgba(13, 13, 14, 0.12)] border-solid border-[1px] transition-all duration-200
            hover:bg-gtred hover:text-[#ffffff]
            hover:border-gtred hover:shadow-[0_0_16px_0_rgba(224,64,20,0.5)]
        `,
        white_ne: `
            bg-[#ffffff] text-gscale-950 font-normal
            border-[rgba(13, 13, 14, 0.12)] border-solid border-[1px] transition-all duration-200
            hover:bg-gtred hover:text-[#ffffff]
            hover:border-gtred
        `,
        no_border: `
            bg-[#ffffff] text-gtred font-normal
            transition-all duration-200
            hover:bg-gtred/80 hover:text-white
        `,
        border_orange: `
          bg-[#ffffff] text-gtred font-normal
          border-gtred/80 border-solid border-[1px] transition-all duration-200
          hover:bg-gray-100 hover:text-gtred
          hover:border-gtred/80
        `,
        simple: `
            bg-white text-black font-normal
            border border-gray-200
            transition-all duration-200
            hover:bg-gray-100
        `,
      },
      size: {
        default: 'px-2 py-2',
        small: 'px-1 py-1',
        large: 'px-4 py-4',
        full: 'w-full',
        none: 'px-0 py-0',
        long: 'px-2 py-1',
        xlong: 'px-4 py-2',
      },
      rounded: {
        default: 'rounded-md',
        small: 'rounded-sm',
        large: 'rounded-lg',
        full: 'rounded-full',
        none: 'rounded-none',
      },
    },
    defaultVariants: {
      variant: 'normal',
      size: 'default',
      rounded: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof variants> {}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, rounded, children, disabled, ...props },
    ref,
  ) => {
    const Comp = 'button';

    return (
      <Comp
        className={cn(
          variants({ variant, size, rounded, className }),
          disabled && 'pointer-events-none bg-gray-300 text-gray-500',
        )}
        disabled={disabled}
        ref={ref}
        {...props}
      >
        <div className="pointer-events-none flex items-center justify-center">
          {children}
        </div>
      </Comp>
    );
  },
);

Button.displayName = 'Button';

export { Button };
